import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { Globals } from '../globals';
import { AuthService } from './auth.service';

@Injectable({
  providedIn: 'root',
})
export class TokenRegenerationService {
  private isRefreshing = false;

  constructor(
    private readonly router: Router,
    private readonly authService: AuthService,
    private readonly globals: Globals
  ) {
    this.initializeClickDetection();
  }

  private initializeClickDetection(): void {
    // Listen for all click events on the document
    document.addEventListener('click', () => {
      this.checkAndRefreshToken();
    });
  }

  private checkAndRefreshToken(): void {
    // Skip if already refreshing or user not logged in
    if (this.isRefreshing || !this.authService.isUserLoggedIn()) {
      return;
    }

    // Check if token expires in 5 minutes or less
    if (this.tokenExpiresSoon()) {
      this.refreshToken();
    }
  }

  public refreshToken(): void {
    if (this.isRefreshing) {
      return;
    }

    this.isRefreshing = true;

    this.authService.refreshToken().subscribe({
      next: (result) => {
        this.isRefreshing = false;
      },
      error: (error) => {
        this.isRefreshing = false;
        // If refresh fails, logout the user
        this.authService.logout(true);
      },
    });
  }

  public isTokenRefreshing(): boolean {
    return this.isRefreshing;
  }

  tokenExpiresSoon(): boolean {
    if (!this.authService.isUserLoggedIn()) {
      return false;
    }

    const currentUser = this.globals.getUser();
    if (!currentUser.expirationTime) {
      return false;
    }

    const expirationTime = Number(currentUser.expirationTime);
    const currentTime = new Date().getTime();
    const thirtyMinutesInMs = 30 * 60 * 1000;

    // Return true if token expires in 30 minutes or less
    return expirationTime - currentTime <= thirtyMinutesInMs;
  }
}
